import {
  BaseCharacterMeshConfig,
  HELMET_TEXTURE_MAPPING,
  HelmetTexture,
  HelmetType,
  WEAPON_TEXTURE_MAPPING,
  WeaponTexture,
  WeaponType,
} from "mmo-common";

interface FlattenedBaseCharacterMeshConfigExtra<
  T extends HelmetType,
  K extends WeaponType
> {
  ["helmet.helmetType"]: T | null;
  ["helmet.texture"]: HelmetTexture<T> | null;
  ["weapon.weaponType"]: K | null;
  ["weapon.texture"]: WeaponTexture<K> | null;
}

export type FlattenedBaseCharacterMeshConfig = Omit<
  BaseCharacterMeshConfig,
  "helmet" | "weapon"
> &
  FlattenedBaseCharacterMeshConfigExtra<HelmetType, WeaponType>;

export const toFlattenedCharacterConfig = (
  characterConfig: BaseCharacterMeshConfig
): FlattenedBaseCharacterMeshConfig => ({
  ...characterConfig,
  "helmet.helmetType": characterConfig.helmet?.helmetType ?? null,
  "helmet.texture": characterConfig.helmet?.texture ?? null,
  "weapon.weaponType": characterConfig.weapon?.weaponType ?? null,
  "weapon.texture": characterConfig.weapon?.texture ?? null,
});

export const fromFlattenedCharacterConfig = (
  flattened: FlattenedBaseCharacterMeshConfig
): BaseCharacterMeshConfig => ({
  ...flattened,
  ...(flattened["weapon.weaponType"]
    ? {
        weapon: {
          weaponType: flattened["weapon.weaponType"],
          texture:
            ((
              WEAPON_TEXTURE_MAPPING[
                flattened["weapon.weaponType"]
              ] as any as string[]
            ).includes(flattened["weapon.texture"] as any)
              ? flattened["weapon.texture"]
              : null) ??
            WEAPON_TEXTURE_MAPPING[flattened["weapon.weaponType"]][0],
        },
      }
    : {
        weapon: null,
      }),
  ...(flattened["helmet.helmetType"]
    ? {
        helmet: {
          helmetType: flattened["helmet.helmetType"],
          texture:
            ((
              HELMET_TEXTURE_MAPPING[
                flattened["helmet.helmetType"]
              ] as any as string[]
            ).includes(flattened["helmet.texture"] as any)
              ? flattened["helmet.texture"]
              : null) ??
            HELMET_TEXTURE_MAPPING[flattened["helmet.helmetType"]][0],
        },
      }
    : { helmet: null }),
});

export type BaseCharacterConfigChanges = Partial<{
  [k in keyof FlattenedBaseCharacterMeshConfig]: {
    oldValue: FlattenedBaseCharacterMeshConfig[k];
    newValue: FlattenedBaseCharacterMeshConfig[k];
  };
}>;

/**
 * Gets the changes between two character configs
 */
export function getBaseCharacterConfigChanges(
  oldConfig: BaseCharacterMeshConfig,
  newConfig: BaseCharacterMeshConfig
) {
  const oldFlattened = toFlattenedCharacterConfig(oldConfig);
  const newFlattened = toFlattenedCharacterConfig(newConfig);

  const changes: BaseCharacterConfigChanges = {};
  for (const [k, v] of Object.entries(oldFlattened)) {
    const field = k as keyof FlattenedBaseCharacterMeshConfig;
    const oldValue =
      v as FlattenedBaseCharacterMeshConfig[keyof FlattenedBaseCharacterMeshConfig];
    if (newFlattened[field] !== oldValue) {
      changes[field] = {
        oldValue,
        newValue: newFlattened[field],
      } as any;
    }
  }
  return changes;
}
